import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ChevronDown, Download, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import InvoiceStatusChip from '../../components/crm/InvoiceStatusChip';
import EmptyState from '../../components/crm/EmptyState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import VoidInvoiceDialog from '../../components/admin/VoidInvoiceDialog';
import Modal from '../../components/admin/Modal';
import InvoiceTemplateFrame from '../../components/financial/InvoiceTemplateFrame';
import PaymentSchedulePanel from '../../components/financial/PaymentSchedulePanel';
import NewInvoiceDialog from '../../components/admin/NewInvoiceDialog';
import BackLink from '../../components/admin/BackLink';
import { fmtCents, invoiceStatusMeta, INVOICE_TRANSITIONS, PAYMENT_METHODS } from '../../lib/financial';

export default function InvoiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [rawInvoice, setRawInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [account, setAccount] = useState(null);
  const [signature, setSignature] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  // Pending-payment-delete state holds the payment object the user clicked
  // delete on; ConfirmDialog reads its open prop from this. Replaces the
  // earlier window.confirm() call so the UX matches the rest of the app.
  const [pendingPaymentDelete, setPendingPaymentDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  // Transient in-app banner for status-change + error messages — replaces
  // the browser-native alert() calls. Auto-clears after 4 seconds. kind:
  // 'success' (green) | 'error' (red).
  const [flash, setFlash] = useState(null);
  const showFlash = useCallback((message, kind = 'success') => {
    setFlash({ message, kind, key: Date.now() });
  }, []);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/financial/invoices/${id}`);
      setInvoice(res.invoice);
      setRawInvoice(res.raw || null);
      setPayments(res.payments || []);
      setLineItems(res.line_items || []);
      setAccount(res.account || null);
      setSignature(res.signature || null);
      setPaymentSchedule(res.payment_schedule || []);
      setRevisions(res.revisions || []);
    } catch (err) {
      setError(err?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (target) => {
    setStatusOpen(false);
    try {
      await api.put(`/api/admin/financial/invoices/${id}`, { status: target });
      await load();
      showFlash(`Invoice marked as ${target}.`, 'success');
    } catch (err) { showFlash(err?.message || 'Status change failed', 'error'); }
  };
  const deleteInvoice = async () => {
    await api.delete(`/api/admin/financial/invoices/${id}`);
    navigate('/admin/invoices');
  };
  // Mirrors the API rule: hard delete is for unused drafts only.
  const canHardDelete = !!invoice && invoice.status === 'draft'
    && !invoice.sentAt && !invoice.signedAt && !invoice.voidedAt && !(payments && payments.length > 0);
  const onPayment = async (body) => {
    try {
      const res = await api.post('/api/admin/financial/payments', { invoiceId: id, ...body });
      await load();
      setPayOpen(false);
      if (res.invoiceStatusChanged) {
        showFlash(`Payment recorded. Invoice marked ${res.invoiceStatus}.`, 'success');
      } else {
        showFlash('Payment recorded.', 'success');
      }
    } catch (err) { showFlash(err?.message || 'Payment failed', 'error'); }
  };
  const requestDeletePayment = (p) => {
    setPendingPaymentDelete(p);
  };
  const confirmDeletePayment = async () => {
    if (!pendingPaymentDelete) return;
    setDeletingPayment(true);
    try {
      await api.delete(`/api/admin/financial/payments/${pendingPaymentDelete.id}`);
      await load();
      setPendingPaymentDelete(null);
      showFlash('Payment removed.', 'success');
    } catch (err) {
      showFlash(err?.message || 'Delete failed', 'error');
    } finally {
      setDeletingPayment(false);
    }
  };

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" description="Invoice views are restricted to staff." />;
  }

  // Void is its own admin-only action (dialog + /void route with cancellation
  // side effects), so it is not offered as a plain status flip.
  const allowed = invoice ? (INVOICE_TRANSITIONS[invoice.status] || []).filter((s) => s !== 'void') : [];
  const canVoid = user?.role === 'nexsav_admin' && invoice && invoice.status !== 'void' && !invoice.voidedAt;

  return (
    <div className="max-w-4xl mx-auto">
      <BackLink to="/admin/invoices" className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to invoices
      </BackLink>
      {loading && !invoice && <div className="animate-pulse h-20 rounded-xl bg-white/[0.03]" />}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}
      {invoice && (
        <>
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight font-mono">{invoice.invoiceNumber}</h1>
              <div className="text-[12px] text-gray-500 mt-1">Issued {invoice.issuedAt}{invoice.dueAt ? ` · Due ${invoice.dueAt}` : ''}</div>
            </div>
            <div className="relative">
              <button onClick={() => setStatusOpen((v) => !v)} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/[0.03]">
                <InvoiceStatusChip status={invoice.displayStatus || invoice.status} />
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              </button>
              {statusOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setStatusOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-xl border border-white/[0.08] bg-[#0a0a0f] p-1.5">
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider px-2 py-1">Move to</div>
                    {allowed.length === 0 && <div className="px-2 py-2 text-[12px] text-gray-500">No valid transitions</div>}
                    {allowed.map((s) => (
                      <button key={s} onClick={() => changeStatus(s)} className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-gray-300 hover:bg-white/[0.04] hover:text-white">
                        {invoiceStatusMeta(s).label}
                      </button>
                    ))}
                    {canVoid && (
                      <>
                        <div className="border-t border-white/[0.05] my-1" />
                        <button onClick={() => { setStatusOpen(false); setVoidOpen(true); }} data-testid="invoice-void-open"
                          className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-red-300 hover:bg-red-500/10">
                          Void invoice…
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Amount</div>
                <div className="text-[20px] text-white font-semibold">{fmtCents(invoice.amountCents, invoice.currency)}</div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Paid</div>
                <div className="text-[20px] text-emerald-300 font-semibold">{fmtCents(invoice.paidCents, invoice.currency)}</div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Outstanding</div>
                <div className={`text-[20px] font-semibold ${invoice.isOverdue ? 'text-red-300' : 'text-white'}`}>{fmtCents(invoice.outstandingCents, invoice.currency)}</div>
              </div>
            </div>
            {invoice.isOverdue && <div className="text-[12px] text-red-300 mt-3">This invoice is overdue.</div>}
            {invoice.notes && <div className="text-[12px] text-gray-400 mt-3 leading-relaxed">{invoice.notes}</div>}
            <div className="flex items-center gap-2 mt-4">
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                data-testid="invoice-edit"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38]/[0.15] hover:bg-[#B0FC38]/[0.25] text-[#B0FC38] border border-[#B0FC38]/30"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <a
                href={`/api/invoices/${invoice.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="invoice-export-pdf"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/[0.08]"
              >
                <Download className="w-3.5 h-3.5" /> Export PDF
              </a>
              {revisions.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRevisions((v) => !v)}
                  data-testid="invoice-revisions-toggle"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-white/[0.02] hover:bg-white/[0.06] text-gray-300 border border-white/[0.06]"
                >
                  Revisions ({revisions.length})
                </button>
              )}
            </div>
          </div>

          {showRevisions && revisions.length > 0 && (
            <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6" data-testid="invoice-revisions">
              <h3 className="text-[13px] font-semibold text-white mb-3">Revision history</h3>
              <ul className="space-y-1.5 text-[12px]">
                {revisions.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border border-white/[0.04] bg-white/[0.02]">
                    <span className="text-gray-300 font-mono text-[11px]">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                    <span className="text-gray-500 text-[11px]">{r.revised_by_user_id?.slice(0, 8) || '—'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mb-6">
            <InvoiceTemplateFrame
              invoice={rawInvoice || invoice}
              lineItems={lineItems}
              account={account}
              signature={signature}
            />
          </div>

          {paymentSchedule.length > 0 && (
            <div className="mb-6">
              <PaymentSchedulePanel schedule={paymentSchedule} currency={invoice.currency} />
            </div>
          )}

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[13px] font-semibold text-white">Payments ({payments.length})</h3>
              <button onClick={() => setPayOpen(true)} disabled={invoice.status === 'draft' || invoice.status === 'void'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-40">
                <Plus className="w-3 h-3" /> Record payment
              </button>
            </div>
            {payments.length === 0 ? (
              <div className="text-[12px] text-gray-500">No payments recorded.</div>
            ) : (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/[0.06] bg-white/[0.02]">
                    <div>
                      <div className="text-[13px] text-white">{fmtCents(p.amountCents, invoice.currency)}</div>
                      <div className="text-[11px] text-gray-500">{p.paidAt}{p.method ? ` · ${p.method}` : ''}{p.reference ? ` · ref ${p.reference}` : ''}</div>
                    </div>
                    <button onClick={() => requestDeletePayment(p)} className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10" aria-label="Delete payment">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {user?.role === 'nexsav_admin' && (
            <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
              <h3 className="text-[13px] font-semibold text-white mb-2">Danger zone</h3>
              {canHardDelete ? (
                <>
                  <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20">
                    <Trash2 className="w-3 h-3" /> Delete draft
                  </button>
                  <div className="text-[11px] text-gray-500 mt-2">Permanently removes this unused draft.</div>
                </>
              ) : (
                <div className="text-[11px] text-gray-500">
                  Issued invoices can't be deleted — void them instead so payment history is preserved.
                </div>
              )}
            </div>
          )}

          <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} onSubmit={onPayment}
            maxAmount={invoice.outstandingCents} currency={invoice.currency} />
          <VoidInvoiceDialog open={voidOpen} invoiceId={id} onClose={() => setVoidOpen(false)}
            onVoided={async () => { setVoidOpen(false); await load(); showFlash('Invoice voided.', 'success'); }} />

          <NewInvoiceDialog
            open={editOpen}
            mode="edit"
            editTarget={{ ...(rawInvoice || invoice), lineItems: lineItems }}
            onClose={() => setEditOpen(false)}
            onSaved={async () => { setEditOpen(false); await load(); }}
          />

          <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={deleteInvoice}
            title="Delete draft?" description={`Permanently removes draft invoice ${invoice.invoiceNumber}.`}
            confirmLabel="Delete" destructive />

          <ConfirmDialog
            open={!!pendingPaymentDelete}
            onClose={() => !deletingPayment && setPendingPaymentDelete(null)}
            onConfirm={confirmDeletePayment}
            title="Delete payment?"
            description={pendingPaymentDelete
              ? `Removes the ${fmtCents(pendingPaymentDelete.amountCents)} payment from this invoice. The invoice status will be recomputed automatically.`
              : ''}
            confirmLabel={deletingPayment ? 'Deleting…' : 'Delete payment'}
            destructive
          />
        </>
      )}

      {flash && (
        <div
          key={flash.key}
          role="status"
          aria-live="polite"
          data-testid="invoice-flash-banner"
          className={`fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-xl shadow-lg border text-[13px] ${
            flash.kind === 'error'
              ? 'bg-red-500/15 border-red-500/30 text-red-200'
              : 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-[#D7FF8A]'
          }`}
        >
          {flash.message}
        </div>
      )}
    </div>
  );
}

// EditInvoiceModal removed in Sprint K-3 — the full edit form is now
// NewInvoiceDialog rendered with mode="edit" so it shares the create-flow's
// fields (line items, account picker, signature, etc.) plus an editable
// invoice number.

function PaymentModal({ open, onClose, onSubmit, maxAmount, currency }) {
  const [amount, setAmount] = useState(maxAmount / 100);
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('check');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        amountCents: Math.round(Number(amount) * 100),
        paidAt, method, reference: reference.trim() || null,
      });
    } finally { setSubmitting(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Record payment">
      <div className="space-y-4">
        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Amount ({currency})</div>
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white" />
          <div className="text-[11px] text-gray-600 mt-1">Outstanding: {fmtCents(maxAmount, currency)}</div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Paid at</div>
            <input value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white" />
          </label>
          <label className="block">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Method</div>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white">
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Reference (optional)</div>
          <input value={reference} onChange={(e) => setReference(e.target.value)}
            placeholder="Check number / wire ref / PO #"
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white" />
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={submitting || Number(amount) <= 0}
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            {submitting ? 'Recording…' : 'Record'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
